<template>
  <ContentContainer :loadingStatus="isLoading">
    <div class="promotion-list content-wrapper">
      <div class="page-header">{{ $t('promotion') }}
        <popover-preview-remark-component
          :hotel-id="activeHotelId"
          :remark-type="REMARK_TYPE.PROMOTION">
        </popover-preview-remark-component>
      </div>
      <div>
        <el-row>
          <el-col :span="4">
            <el-input
              :placeholder="$t('keyword')"
              v-model="keyword"
              @input="isSearching = true"></el-input>
          </el-col>
          <el-col :span="20" style="text-align: right">
              <el-button type="primary"
                         icon="el-icon-plus"
                         @click="onAddPromotionButtonClick"> {{ $t('newPromotion') }}</el-button>
          </el-col>
        </el-row>
        <div style="margin-top: 25px">
          <el-table
            id="promotion-list-table"
            :data="promotionListDataSource"
            :row-class-name="getRowClassName"
            :default-sort = "{prop: 'code', order: 'ascending'}"
            v-loading="isSearching"
            style="width: 100%"
            header-row-class-name="table-header-color">
            <el-table-column
              :label="$t('isDefault')"
              width="90px">
              <template slot-scope="scope">
                <el-checkbox v-model="isDefaultList[scope.row.id]" :label="scope.row.id" @change="onDefaultChange(scope.row.id)"> &nbsp; </el-checkbox>
              </template>
            </el-table-column>
            <el-table-column
              prop="code"
              :label="$t('code')">
            </el-table-column>
            <el-table-column
              :label="$t('detail')">
              <template slot-scope="scope">
                <span v-if="scope.row.detail !== '' && scope.row.detail !== undefined">{{ scope.row.detail }}</span>
                <span v-if="scope.row.detail === '' || scope.row.detail === undefined">-</span>
              </template>
            </el-table-column>
            <el-table-column
              :label="$t('periodOfStay')">
              <template slot-scope="scope">
                <span v-if="scope.row.periodOfStayList.length === 0">-</span>
                <div v-for="(periodOfStay, index) of scope.row.periodOfStayList" :key="`period-${index}`">
                  <span> {{bindPeriodOfStay(periodOfStay.startDate, periodOfStay.endDate)}}</span>
                </div>
              </template>
            </el-table-column>
            <el-table-column
              :label="$t('applyRoomType')"
              align="center">
              <template slot-scope="scope">
                <div v-if="bindApplyRoomType(scope.row) !== false">
                  <el-popover :placement="'right'" width="400" trigger="click" style="padding: 0 !important">
                    <div class="apply-room-type-popover-header el-card__header">
                      <span>{{$t('applyRoomType')}}</span>
                    </div>
                    <div class="apply-room-type-popover-body">
                      <ul v-if="applyRoomTypeListPopover(scope.row).length > 0"
                          style="padding: 0 15px; margin: 0">
                        <li v-for="(applyRoomType, index) in applyRoomTypeListPopover(scope.row)" :key="index">
                          <strong>[{{applyRoomType.code}}]</strong> {{applyRoomType.name}}
                        </li>
                      </ul>
                      <div v-if="applyRoomTypeListPopover(scope.row).length === 0"
                           class="no-data-apply-room-type">
                        {{ $t('applyRoomTypeNoData') }}
                      </div>
                    </div>
                    <div class="apply-room-type apply-specific-room-type" slot="reference">
                      <span type="primary" slot="reference">{{bindApplyRoomType(scope.row)}}</span>
                    </div>
                  </el-popover>
                </div>
                <div class="apply-room-type apply-all-room-type"
                     v-if="bindApplyRoomType(scope.row) === false">
                  <span>{{$t('applyAllRoomType')}}</span>
                </div>
              </template>
            </el-table-column>
            <el-table-column
              :label="$t('night')"
              width="80px">
              <template slot-scope="scope">
                <div>{{$t('min')}}: {{ scope.row.minNight }}</div>
                <div>{{$t('max')}}: {{ scope.row.maxNight }}</div>
              </template>
            </el-table-column>
            <el-table-column
              :label="$t('status')"
              width="80px">
              <template slot-scope="scope">
                <el-tag :type="scope.row.isEnable === 1 ? 'success' : 'danger'" size="mini" disable-transitions>
                  {{ getPromotionStatus(scope.row.isEnable) }}
                </el-tag>
              </template>
            </el-table-column>
            <el-table-column align="center">
              <template slot-scope="scope">
                <el-button type="primary" size="small" icon="el-icon-edit" plain circle @click="onGotoEditButtonClick(scope.row)"></el-button>
                <el-button type="danger" size="small" icon="el-icon-delete" plain circle @click="onRemovePromotionButtonClick(scope.row)"></el-button>
              </template>
            </el-table-column>
          </el-table>
        </div>
      </div>
    </div>
  </ContentContainer>
</template>

<script>
import i18nMessages from '@/translations/Hotel/ManageHotel/Promotion/List.i18n'
import AlertViewMixin from '@/mixins/alert-view-mixin'
import LoadingViewMixin from '@/mixins/loading-view-mixin'
import NotificationMixin from '@/mixins/notification-mixin'
import HotelPromotionApi from '@/api/hotel/promotion-api'
import ContentContainer from '@/components/ContentContainerComponent.vue'
import PopoverPreviewRemarkComponent from '../../components/PopoverPreviewRemarkComponent'
import LocalStorageFactory from '@/factories/local-storage-factory'
import { getHotelIndexRoute } from '@/services/hotel-service'
import { filterDataByKeyword } from '@/services/general-service'
import { getStatusName } from '@/helpers'
import { get, debounce } from 'lodash'
import { REMARK_TYPE } from '@/configs/constant'
import { mapState } from 'vuex'
import { dateFormat } from '@/helpers/datetime'

export default {
  name: 'HotelPromotionList',
  mixins: [AlertViewMixin, LoadingViewMixin, NotificationMixin],
  components: { ContentContainer, PopoverPreviewRemarkComponent },
  i18n: { messages: i18nMessages },
  data () {
    return {
      REMARK_TYPE,
      isLoading: true,
      keyword: '',
      isSearching: false,
      promotionList: [],
      roomTypeList: [],
      isDefaultList: {},
      promotionListDataSource: []
    }
  },
  watch: {
    keyword: debounce(function () {
      this.isSearching = false
    }, 300),
    isSearching: function (value) {
      if (!value) {
        this.getPromotionListDataSource()
      }
    }
  },
  created: async function () {
    if (this.activeHotelId === undefined || this.activeHotelId === '') {
      const companyDetail = LocalStorageFactory.getCompanyDetail()
      const routeName = await getHotelIndexRoute(companyDetail, this.$store)

      return this.$router.push({
        name: routeName
      })
    }

    try {
      this.isLoading = true
      const result = await HotelPromotionApi.list(this.activeHotelId)
      this.promotionList = result.promotionList
      this.roomTypeList = result.roomTypeList
      this.isDefaultList = this._prepareIsDefaultList()
      this.getPromotionListDataSource()
    } catch (err) {
      this.alertError(err.message)
    }

    this.isLoading = false
  },
  computed: {
    ...mapState({
      activeHotelId: state => state.general.activeHotelId
    })
  },
  methods: {
    getPromotionListDataSource: function () {
      const searchKeys = ['code', 'detail']
      const filteredPromotionList = filterDataByKeyword(this.promotionList, this.keyword, searchKeys)
      this.promotionListDataSource = [...filteredPromotionList]
    },
    getPromotionStatus: function (status) {
      return this.$t(getStatusName(status))
    },
    onGotoEditButtonClick: function (promotion) {
      this.$router.push({
        name: 'HotelPromotionEdit',
        params: {
          promotion: promotion,
          roomTypeList: this.roomTypeList
        }
      })
    },
    onAddPromotionButtonClick: function () {
      this.$router.push({
        name: 'HotelPromotionAdd',
        params: {
          roomTypeList: this.roomTypeList
        }
      })
    },
    onRemovePromotionButtonClick: async function (row) {
      const confirmMessage = this.$t('confirmRemovePromotionMessage', {
        promotionCode: row.code
      })
      const confirmButtonText = this.$t('confirmButtonText')
      const cancelButtonText = this.$t('cancelButtonText')
      const isConfirmRemove = await this.alertConfirmDelete(confirmMessage, confirmButtonText, cancelButtonText)

      if (!isConfirmRemove) {
        return false
      }

      this.removePromotion(row.id)
    },
    removePromotion: async function (promotionId) {
      try {
        this.startLoading()

        await HotelPromotionApi.remove(this.activeHotelId, promotionId)

        this.promotionList = this.promotionList.filter(item => item.id !== promotionId)
        this.getPromotionListDataSource()

        const notificationTitle = this.$t('removePromotionSuccess')
        this.showNotificationSuccess(notificationTitle)
      } catch (err) {
        this.alertError(err.message)
      }

      this.stopLoading()
    },
    bindPeriodOfStay: function (startDate, endDate) {
      const fromDate = new Date(startDate)
      const toDate = new Date(endDate)

      return `${dateFormat.short(fromDate)} - ${dateFormat.short(toDate, 'short')}`
    },
    bindApplyRoomType: function (promotion) {
      const roomTypeIdList = get(promotion, 'roomTypeIdList', undefined)
      if (!roomTypeIdList) {
        return false
      }

      const roomTypeCodeList = roomTypeIdList.map(roomTypeId => {
        const roomTypeResult = this.roomTypeList.find(roomType => roomType.id === roomTypeId)
        const roomTypeCode = get(roomTypeResult, 'code', '')

        return roomTypeCode
      })

      const applyRoomTypeCodeList = roomTypeCodeList.filter(item => item !== '')
      const applyRoomTypeCodeStr = (applyRoomTypeCodeList.length > 0) ? `${applyRoomTypeCodeList.join(' / ')}` : 'N/A'

      return applyRoomTypeCodeStr
    },
    applyRoomTypeListPopover: function (promotion) {
      const roomTypeIdList = get(promotion, 'roomTypeIdList', [])
      const roomTypeList = roomTypeIdList.map(roomTypeId => {
        const roomTypeResult = this.roomTypeList.find(roomType => roomType.id === roomTypeId)
        const roomTypeCode = get(roomTypeResult, 'code', '')
        const roomTypeName = get(roomTypeResult, 'name', '')

        return {
          code: roomTypeCode,
          name: roomTypeName
        }
      })

      const applyRoomTypeList = roomTypeList.filter(item => item.code !== '' && item.name !== '')
      return applyRoomTypeList
    },
    getRowClassName: function ({ row, rowIndex }) {
      if (row.isEnable === 1) {
        return ''
      }
      return 'disable-row'
    },
    onDefaultChange: async function (promotionId) {
      this.startLoading()
      try {
        const isDefault = (this.isDefaultList[promotionId] === true) ? 1 : 0
        await this._updateIsDefault(promotionId, isDefault)

        const title = this.$t('updateIsDefaultSuccessTitle')
        const message = this.$t('updateIsDefaultSuccessMessage')
        this.showNotificationSuccess(title, message)
      } catch (err) {
        this.alertError(err.message)
      }
      this.stopLoading()
    },
    _updateIsDefault: async function (promotionId, isDefault) {
      const data = {
        isDefault: isDefault
      }
      await HotelPromotionApi.updateIsDefault(this.activeHotelId, promotionId, data)
    },
    _prepareIsDefaultList: function () {
      const isDefaultList = {}
      this.promotionList.forEach((promotion) => {
        const promotionId = promotion.id
        isDefaultList[promotionId] = Boolean(get(promotion, 'isDefault', false))
      })

      return isDefaultList
    }
  }
}
</script>

<style scoped>
  #promotion-list-table {
    width: 100%;
    border: 1px solid #dddddd;
    border-radius: 5px;
    box-shadow: 0px 3px 5px 1px #eeeeee;
  }
  .el-table th {
    background-color: #eeeeee;
  }
  .el-form > div {
    text-align: left;
  }

  .apply-room-type-popover-body ul {
    list-style-type: none;
  }

  .apply-room-type-popover-body ul li:before {
    content: '-';
    position: absolute;
    margin-left: -20px;
  }

  .apply-room-type {
    border-radius: 5px;
    line-height: 30px;
    font-size: 12px;
    padding: 0 10px;
    color: #6ab2ff;
    background-color: #fafcff;
  }

  .apply-all-room-type {
    border: 1px solid #fafcff;
    font-weight: 800;
  }

  .apply-specific-room-type {
    font-weight: 800;
    border: 1px solid #fafcff;
    cursor: pointer;
  }

  .apply-specific-room-type:hover {
    color: #409eff;
    background-color: #edf5ff;
    border: 1px solid #b3d8ff;
  }

  .apply-specific-room-type:focus {
    color: #409eff;
    background-color: #edf5ff;
    border: 1px solid #b3d8ff;
  }

  .apply-room-type-popover-header {
    padding: 10px;
    border-bottom: 1px solid #EBEEF5;
    font-size: 15px;
    background-color: #f8f9fa;
  }

  .apply-room-type-popover-body {
    padding: 10px;
  }

  .no-data-apply-room-type {
    text-align: center;
    font-style: italic;
    color: #b7b9bd;
  }
</style>
